import ufeApi from 'services/api/ufeApi';
import authUtils from 'utils/Authentication';
import RCPSCookies from 'utils/RCPSCookies';
import userUtils from 'utils/User';
import Storage from 'utils/localStorage/Storage';
import LOCAL_STORAGE from 'utils/localStorage/Constants';

async function refreshToken(bodyJSON, options = {}) {
    const { newRefreshTokenFlow } = Sephora.configurationSettings;
    const BACKOFF_DELAYS = [2000, 4000, 6000];

    // Returns the latest access token from storage (or undefined)
    const getLatestAccessToken = () => {
        try {
            const parsed = Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN);

            return parsed;
        } catch {
            return undefined;
        }
    };

    const getLatestRefreshToken = () => {
        try {
            return Storage.local.getItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN);
        } catch {
            return undefined;
        }
    };

    async function makeRequestWithTokens(payload, headers, url) {
        return ufeApi
            .makeRequest(url, {
                url,
                method: 'POST',
                body: JSON.stringify(payload),
                headers
            })
            .then(data => {
                if (data.errorCode || data.errors) {
                    return Promise.reject(data);
                }

                // Save tokens back to localStorage
                if (data.accessToken) {
                    Storage.local.setItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN, data.accessToken);
                }

                if (data.refreshToken) {
                    Storage.local.setItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN, data.refreshToken);
                }

                return data;
            });
    }

    async function buildRequestConfig(accessTokenOverride, refreshTokenOverride) {
        let url = '/api/auth/v1/refreshToken';
        let headers = {
            'Content-type': 'application/json',
            'x-requested-source': 'web'
        };

        if (RCPSCookies.isRCPSAuthEnabled()) {
            url = '/gway/v1/dotcom/auth/v1/refreshToken';
            headers.deviceId = await authUtils.getFingerPrint();

            if (options.headers) {
                headers = {
                    ...headers,
                    ...options.headers
                };
            }
        }

        const accessToken = accessTokenOverride || Storage.local.getItem(LOCAL_STORAGE.AUTH_ACCESS_TOKEN) || bodyJSON.accessToken;
        const refreshTokenLocal = refreshTokenOverride || Storage.local.getItem(LOCAL_STORAGE.AUTH_REFRESH_TOKEN) || bodyJSON.refreshToken;

        let payload = {
            ...bodyJSON,
            accessToken,
            refreshToken: refreshTokenLocal
        };

        if (newRefreshTokenFlow) {
            url = '/gway/v1/dotcom/auth/v2/refreshToken';
            const email = bodyJSON.email || userUtils.getProfileEmail();
            payload = {
                ...(email && { email }),
                accessToken,
                refreshToken: refreshTokenLocal
            };

            headers = options.headers ? { ...headers, ...options.headers } : headers;
        }

        return { url, headers, payload };
    }

    const attemptRequest = async (attempt = 0, lastAccessToken = bodyJSON.accessToken) => {
        try {
            const { url, headers, payload } = await buildRequestConfig();

            return await makeRequestWithTokens(payload, headers, url);
        } catch (err) {
            let status = err?.responseStatus || err?.errorCode;

            const rawMsg = err?.errors?.[0]?.errorMessage;
            const normalizedMsg = typeof rawMsg === 'string' ? rawMsg.trim().toLowerCase() : '';

            if (normalizedMsg.includes('invalid token')) {
                status = 403;
            }

            if (status === 403 && attempt < BACKOFF_DELAYS.length) {
                await new Promise(res => setTimeout(res, BACKOFF_DELAYS[attempt]));

                const latestAccessToken = getLatestAccessToken();

                const tokenChanged = latestAccessToken && latestAccessToken !== lastAccessToken;

                if (tokenChanged) {
                    // Another tab has already refreshed the token
                    Sephora.logger.verbose('[Refresh] Token was refreshed by another tab. Making final sync call from refreshToken.js.');

                    try {
                        const finalAccessToken = getLatestAccessToken();
                        const finalRefreshToken = getLatestRefreshToken();
                        const email = bodyJSON.email || userUtils.getProfileEmail();
                        const { url, headers } = await buildRequestConfig(finalAccessToken, finalRefreshToken);

                        const finalPayload = {
                            accessToken: finalAccessToken,
                            refreshToken: finalRefreshToken,
                            ...(email && { email })
                        };

                        const finalData = await makeRequestWithTokens(finalPayload, headers, url);
                        Sephora.logger.verbose('[Refresh] Final sync call success refreshToken.js:', finalData);

                        return finalData;
                    } catch (syncErr) {
                        Sephora.logger.verbose('[Refresh] Final sync call failed, assuming token already refreshed from refreshToken.js.');

                        return { tokenRefreshedByOtherTab: true };
                    }
                }

                return attemptRequest(attempt + 1, latestAccessToken || lastAccessToken);
            }

            throw err;
        }
    };

    return attemptRequest();
}

export default {
    refreshToken
};
